import { useCallback, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AccountContext } from "auth/accountProvider";
import { type CpiAuth } from "lib/cpiQueries";

// Resolve the session tokens the CPI/upload endpoints expect. Mirrors the
// useCpiAuth helper in overviewData.ts (kept local to avoid coupling).
const useCpiAuth = (): (() => Promise<CpiAuth>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<CpiAuth> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

export interface FuelEmissionFactor {
  id: string;
  fuelType: string;
  wtwFactor: number;
  ttwFactor: number;
  wttFactor: number;
  unit: string;
  periodStartDate: string;
  periodEndDate: string | null;
  source: string | null;
}

export interface ModalityEmissionFactor {
  id: string;
  transport: string;
  modality: string;
  modalityDetail: string;
  wtwFactor: number;
  ttwFactor: number;
  wttFactor: number;
  periodStartDate: string;
  periodEndDate: string | null;
  source: string | null;
}

export const useFuelEmissionFactors = () => {
  const getAuth = useCpiAuth();
  return useQuery({
    queryKey: ["emission-factors", "fuel"] as const,
    queryFn: async ({ signal }): Promise<FuelEmissionFactor[]> => {
      const { jwtToken, sessionId } = await getAuth();
      const res = await axios.get<{ factors: FuelEmissionFactor[] }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/emission-factors/fuel`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            "X-Session-Id": sessionId,
          },
          signal,
        }
      );
      return res.data.factors ?? [];
    },
  });
};

export const useModalityEmissionFactors = () => {
  const getAuth = useCpiAuth();
  return useQuery({
    queryKey: ["emission-factors", "modality"] as const,
    queryFn: async ({ signal }): Promise<ModalityEmissionFactor[]> => {
      const { jwtToken, sessionId } = await getAuth();
      const res = await axios.get<{ factors: ModalityEmissionFactor[] }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/emission-factors/modality`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            "X-Session-Id": sessionId,
          },
          signal,
        }
      );
      return res.data.factors ?? [];
    },
  });
};
